import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Portal, Typography } from '@mui/material';
import Footer from 'dashboard/components/footer/FooterAdmin';
import Navbar from 'dashboard/components/navbar/NavbarAdmin';
import CommandPalette from 'dashboard/components/commandPalette/CommandPalette';
import Sidebar from 'dashboard/components/sidebar/Sidebar';
import { SidebarContext } from 'dashboard/contexts/SidebarContext';
import routes from 'pages/routes';
import 'dashboard/assets/css/App.css';
import UnAuthorized from 'dashboard/views/admin/main/others/unAuthorized';
import { API } from 'utilities/api';
import { PageChromeProvider } from 'contexts/PageChromeContext';
import { useWhiteLabel } from 'contexts/WhiteLabelContext';
import PageChromeBar from 'dashboard/components/common/PageChromeBar';
import CreateModal from 'dashboard/components/brand/create';
import { useAuth } from 'contexts/AuthContext';
import { useBrand } from 'contexts/BrandContext';

// Synchronously classify the cached user. We use this BEFORE any
// brand chrome paints to decide whether to bounce, render, or wait
// for a profile fetch. Three outcomes:
//   - 'bounce'  — cached admin, no opt-in flag → redirect to /admin
//   - 'render'  — cached non-admin OR opted-in admin → render brand
//   - 'unknown' — no cached user / no admin flag on cache → must fetch
// This lets us avoid the "brand chrome → fetch → redirect" flash on
// first login while still rendering brand instantly for known non-admins.
const classifyCachedUser = () => {
  try {
    const cached = JSON.parse(localStorage.getItem('techproone_user') || 'null');
    if (cached?.is_platform_admin === true) {
      const optedIn = sessionStorage.getItem('tpo_brand_as_admin') === '1';
      return optedIn ? 'render' : 'bounce';
    }
    if (typeof cached?.is_platform_admin === 'boolean') return 'render';
    return 'unknown';
  } catch { return 'render'; }
};

export default function Dashboard(props) {
  const { theme, setTheme, authentication } = props;
  // Classify the cached user once on first render. For auth pages we
  // skip the gate entirely (those need to render with no token).
  const [adminGate, setAdminGate] = useState(() => authentication ? 'render' : classifyCachedUser());

  // Keep ALL hook calls above the early return — React's rules-of-hooks
  // requires hooks to run in the same order every render.
  const router = routes(props).routes;
  router.shift();
  const { ...rest } = props;
  const [fixed] = useState(false);
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const [mini, setMini] = useState(false);
  const [hovered, setHovered] = useState(false);
  const whiteLabel = useWhiteLabel();

  /**
   * The tab says the brand's name, and the icon is the brand's.
   *
   * index.html ships "TechProOne" as the static title and our favicon, and
   * nothing replaced them inside the dashboard. On a brand's own domain that
   * is our name in the tab strip of every person who works there, all day —
   * which is both the most visible leak and the easiest to miss, because the
   * page itself looks perfectly white-labelled.
   */
  useEffect(() => {
    if (!whiteLabel.active) return;
    const name = whiteLabel.brand?.name;
    if (!name) return;
    try { document.title = name; } catch (_) { /* ignore */ }

    const colour = whiteLabel.brand?.brand_color || '#4F46E5';
    const letter = name.trim().charAt(0).toUpperCase() || '•';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">`
      + `<rect width="64" height="64" rx="14" fill="${colour}"/>`
      + `<text x="32" y="44" font-family="system-ui,sans-serif" font-size="36" `
      + `font-weight="700" fill="#fff" text-anchor="middle">${letter}</text></svg>`;
    try {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.type = 'image/svg+xml';
      link.href = `data:image/svg+xml,${encodeURIComponent(svg)}`;
    } catch (_) { /* ignore */ }
  }, [whiteLabel.active, whiteLabel.brand]);
  const navigateRedirect = useNavigate();
  const [token] = useState(localStorage.getItem('techproone_token'));
  const { login } = useAuth();
  const { brandList, selectedBrand, setSelectedBrand, fetchBrands, loaded, loadError, retry } = useBrand();
  // Open on arrival — creating a brand is what a new account is here to do —
  // but closable, with the page behind it still usable.
  const [showCreateBrand, setShowCreateBrand] = useState(true);

  // Bounce immediately for cached admins. For 'unknown' we fall through
  // to the profile-fetch effect below.
  useEffect(() => {
    if (adminGate === 'bounce') window.location.replace('/admin');
  }, [adminGate]);
  // Having the token is enough to render the layout — the brand list
  // resolves async and each page handles its own loading state.
  // Previously we waited on `loaded`, which flashed the 404 page on
  // refresh during the gap before the IAM list returned.
  const validToken = Boolean(token);

  // Fetch the profile to (a) keep the cached user fresh and (b) resolve
  // the admin gate for first-login users where the cache is empty.
  // While the gate is 'unknown' we render a loader (see below) — brand
  // chrome NEVER paints until we know the user isn't an admin.
  useEffect(() => {
    if (!token) return;
    if (adminGate === 'bounce') return;          // already navigating away
    API.getUserProfile()
      .then((profile) => {
        if (!profile || typeof profile !== 'object') {
          setAdminGate('render');
          return;
        }
        login(token, profile);
        if (profile.is_platform_admin === true) {
          const optedIn = (() => {
            try { return sessionStorage.getItem('tpo_brand_as_admin') === '1'; }
            catch { return false; }
          })();
          if (!optedIn) {
            window.location.replace('/admin');
            return;                              // keep showing the loader
          }
        }
        setAdminGate('render');
      })
      .catch(() => setAdminGate('render'));      // fail open — let brand load
  }, [token, login, adminGate]);

  const handleBrandCreated = (newBrandId) => fetchBrands(newBrandId)
    .then(() => navigateRedirect('/dashboard/brand/overview'));

  useEffect(() => {
    if (!token && !authentication) {
      navigateRedirect('/');
    }
  }, [token, authentication, navigateRedirect]);

  // Gate paint on the admin classification. After all hooks have run.
  //   - 'bounce'  → blank bg, useEffect above is firing window.location.replace
  //   - 'unknown' → spinner on the admin-panel bg color so the eventual
  //                 cross-document navigation looks like a single load
  //   - 'render'  → fall through to brand chrome below
  if (adminGate === 'bounce') {
    return <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }} />;
  }
  if (adminGate === 'unknown') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const getRoute = () => window.location.pathname !== '/admin/full-screen-maps';

  const getActiveRoute = (rs) => {
    let active = 'Main Dashboard';
    for (let i = 0; i < rs.length; i++) {
      if (rs[i].collapse) {
        const child = getActiveRoute(rs[i].children);
        if (child !== active) return child;
      } else if (window.location.href.indexOf(rs[i].layout + rs[i].path) !== -1) {
        return rs[i].name;
      }
    }
    return active;
  };

  const getActiveNavbar = (rs) => {
    for (let i = 0; i < rs.length; i++) {
      if (rs[i].collapse) {
        const child = getActiveNavbar(rs[i].children);
        if (child) return child;
      } else if (window.location.href.indexOf(rs[i].layout + rs[i].path) !== -1) {
        return rs[i].secondary;
      }
    }
    return false;
  };

  const getRoutes = (rs) => rs.map((route, key) => {
    if (route.layout === '/admin') {
      return <Route path={route.path} element={route.component} key={key} />;
    }
    if (route.collapse) return getRoutes(route.children);
    return null;
  });

  document.documentElement.dir = 'ltr';

  // Auth pages (reset password / accept-invite) must render WITHOUT a
  // token — that's the whole point. Skip the unauthorized gate when
  // the parent route mounted us with `authentication: true`.
  if (!authentication && !validToken) return <UnAuthorized />;

  const mainBoxWidth = mini === false || (mini === true && hovered === true)
    ? { xs: '100%', xl: 'calc(100% - 290px)' }
    : { xs: '100%', xl: 'calc(100% - 120px)' };

  return (
    <PageChromeProvider>
    <Box sx={{ bgcolor: 'background.default', height: '100vh', width: '100vw' }}>
      <SidebarContext.Provider value={{ toggleSidebar, setToggleSidebar }}>
        {!authentication ? (
          <>
            <Sidebar
              setSelectedBrand={setSelectedBrand}
              selectedBrand={selectedBrand}
              brandList={brandList}
              hovered={hovered}
              setHovered={setHovered}
              mini={mini}
              routes={router}
              {...rest}
            />
            <Box
              sx={{
                float: 'right',
                /**
                 * A column, so the page and the footer can share this height
                 * instead of each claiming all of it.
                 *
                 * They used to be two blocks stacked inside a box exactly one
                 * screen tall, and the page alone asked for `100vh` — so the
                 * footer, and the page's own bottom padding, hung past the
                 * end. Every screen in the dashboard scrolled by about the
                 * height of the footer whether or not it had anything to
                 * scroll, and that scroll pulled the top of the page under
                 * the title bar, which is pinned. Short pages were the worst
                 * of it: a card with nothing below it would still slide up
                 * and lose its own heading.
                 */
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'auto',
                position: 'relative',
                maxHeight: '100%',
                width: mainBoxWidth,
                maxWidth: mainBoxWidth,
                transition: 'all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)',
              }}
            >
              {!loaded ? (
                // Brand list still resolving — show a centered spinner
                // instead of flashing the CreateModal or an empty page.
                <Box sx={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : loadError ? (
                // Not the same as having no brands. Offering to create a
                // first brand to someone whose brand list simply failed to
                // load is both wrong and alarming — and with the page
                // otherwise empty, the footer was the only thing rendered,
                // which is how this looked like a broken layout rather than
                // a failed request.
                <Box
                  sx={{
                    flex: '1 1 auto', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: '10px',
                    textAlign: 'center', px: '24px',
                  }}
                >
                  <Typography variant='h6' fontWeight='700'>
                    We could not load your brands
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ maxWidth: 420 }}>
                    {loadError}
                  </Typography>
                  <Button variant='contained' onClick={() => { retry(); fetchBrands(); }} sx={{ mt: '6px' }}>
                    Try again
                  </Button>
                </Box>
              ) : brandList.length > 0 ? (
                <>
                  <Portal>
                    <Box>
                      <Navbar
                        hovered={hovered}
                        setMini={setMini}
                        mini={mini}
                        logoText={whiteLabel.active ? (whiteLabel.brand?.name || '') : 'TechProOne Dashboard'}
                        brandText={getActiveRoute(router)}
                        secondary={getActiveNavbar(router)}
                        theme={theme}
                        setTheme={setTheme}
                        fixed={fixed}
                        brandList={brandList}
                        selectedBrand={selectedBrand}
                        setSelectedBrand={setSelectedBrand}
                        fetchBrands={fetchBrands}
                        onBrandCreated={handleBrandCreated}
                        {...rest}
                      />
                    </Box>
                  </Portal>
                  {getRoute() && (
                    <Box
                      mx='auto'
                      sx={{
                        px: { xs: '16px', md: '24px' },
                        pb: { xs: '20px', md: '30px' },
                        // Take the room that is left rather than a screenful.
                        // `width: 100%` because a flex column stretches its
                        // children to its own width, and `mx='auto'` then has
                        // nothing to centre.
                        flex: '1 1 auto',
                        width: '100%',
                        pt: 0,
                      }}
                    >
                      <PageChromeBar />
                      <Routes>
                        {getRoutes(router)}
                        <Route path='/' element={<Navigate to='/dashboard/brand/overview' replace />} />
                      </Routes>
                      <Outlet context={[selectedBrand, setSelectedBrand]} />
                      <CommandPalette />
                    </Box>
                  )}
                </>
              ) : (
                /* No brands yet.
                 *
                 * This used to be the bare dialog and nothing else, with the
                 * close button wired to a setter the layout never passed — so
                 * the X rendered and did nothing. Closing it also had nowhere
                 * to land: the dialog was the only thing on the page.
                 *
                 * Now there is a page underneath. Someone who just signed in
                 * can shut the dialog, look around, sign out — and start again
                 * from a button that is still there. */
                <>
                  <Box
                    sx={{
                      flex: '1 1 auto', display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: '12px',
                      textAlign: 'center', px: '24px',
                    }}
                  >
                    <Typography variant='h5' fontWeight='700'>
                      Let’s set up your first brand
                    </Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ maxWidth: 440 }}>
                      A brand is one website, shop and mailbox. You can add more later,
                      and switch between them from the menu.
                    </Typography>
                    <Button
                      variant='contained'
                      onClick={() => setShowCreateBrand(true)}
                      sx={{ mt: '6px' }}
                    >
                      Create a brand
                    </Button>
                  </Box>
                  {showCreateBrand && (
                    <CreateModal
                      add
                      fetchBrands={fetchBrands}
                      onBrandCreated={handleBrandCreated}
                      setShowCreateModal={setShowCreateBrand}
                    />
                  )}
                </>
              )}
              <Box sx={{ flexShrink: 0 }}>
                <Footer />
              </Box>
            </Box>
          </>
        ) : (
          /**
           * The platform-admin layout. Same column as the brand one above,
           * and for the same reason — without it this variant's page and
           * footer both reached for the full height and the window scrolled
           * on every screen.
           */
          <Box sx={{
            display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto',
          }}
          >
            <Portal>
              <Box>
                <Navbar
                  hovered={hovered}
                  setMini={setMini}
                  mini={mini}
                  logoText='TechProOne Dashboard'
                  brandText={getActiveRoute(router)}
                  secondary={getActiveNavbar(router)}
                  theme={theme}
                  setTheme={setTheme}
                  fixed={fixed}
                  {...rest}
                />
              </Box>
            </Portal>
            {getRoute() && (
              <Box
                mx='auto'
                sx={{
                  px: { xs: '16px', md: '24px' },
                  pb: { xs: '20px', md: '30px' },
                  flex: '1 1 auto',
                  width: '100%',
                  pt: 0,
                }}
              >
                <PageChromeBar />
                <Outlet context={[selectedBrand]} />
              </Box>
            )}
            <Box sx={{ flexShrink: 0 }}>
              <Footer />
            </Box>
          </Box>
        )}
      </SidebarContext.Provider>
    </Box>
    </PageChromeProvider>
  );
}
